import React, { useEffect, useState, useCallback } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

// Tokens — match storefront / styles
const ink = "#0A0A0F";
const paper = "#F4F2EC";
const white = "#FFFFFF";
const accent = "#0033FF";
const muted = "rgba(10,10,15,0.58)";
const success = "#22c55e";
const danger = "#ef4444";
const fontMono = "'JetBrains Mono', monospace";
const fontDisplay = "'Archivo Black', 'Archivo', sans-serif";

const STATUS_COLUMNS = [
  { key: "new", label: "New", color: accent },
  { key: "confirmed", label: "Confirmed", color: success },
  { key: "completed", label: "Completed", color: muted },
  { key: "cancelled", label: "Cancelled", color: danger },
];

function fmtAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function fmtSlot(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h)) return hhmm;
  const am = h < 12;
  const display = ((h + 11) % 12) + 1;
  return `${display}:${String(m || 0).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DEFAULT_CONFIG = {
  openDays: [1, 2, 3, 4, 5],
  hours: {
    0: { start: "09:00", end: "21:00" },
    1: { start: "09:00", end: "21:00" },
    2: { start: "09:00", end: "21:00" },
    3: { start: "09:00", end: "21:00" },
    4: { start: "09:00", end: "21:00" },
    5: { start: "09:00", end: "21:00" },
    6: { start: "09:00", end: "21:00" },
  },
  slotMinutes: 60,
  blackoutDates: [],
};

export default function TourRequestsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/employees`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setEmployees(data); })
      .catch(() => {});
  }, []);

  const fetchRows = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/api/tour-requests`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setRows(data);
        setError(null);
      })
      .catch((err) => setError(err.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchRows();
    const id = setInterval(fetchRows, 60000); // poll every 60s for new submissions
    return () => clearInterval(id);
  }, [fetchRows]);

  const updateStatus = async (id, patch) => {
    try {
      const res = await fetch(`${API_URL}/api/tour-requests/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (data && data.id) {
        setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
        if (selected && selected.id === id) setSelected(data);
      }
    } catch (err) {
      console.error("update failed", err);
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this tour request? Use Cancel instead if you want to keep the record.")) return;
    await fetch(`${API_URL}/api/tour-requests/${id}`, { method: "DELETE" });
    setRows((prev) => prev.filter((r) => r.id !== id));
    setSelected(null);
  };

  const counts = STATUS_COLUMNS.reduce((acc, col) => {
    acc[col.key] = rows.filter((r) => r.status === col.key).length;
    return acc;
  }, {});

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: fontDisplay, fontSize: 32, fontWeight: 900, color: ink, letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 6 }}>
            Tour Requests
          </h2>
          <p style={{ fontFamily: fontMono, fontSize: 12, color: muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Studio tour bookings from nycmanufacturing.com/tour · {rows.length} total · auto-refresh 60s
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setSettingsOpen(true)} style={{ ...S.iconBtn, padding: "8px 14px" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
            <span style={{ fontSize: 12, fontFamily: "'Archivo', sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Tour Settings
            </span>
          </button>
          <button onClick={fetchRows} style={{ ...S.iconBtn, padding: "8px 14px" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
            </svg>
            <span style={{ fontSize: 12, fontFamily: "'Archivo', sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Refresh
            </span>
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: "12px 16px", border: `2px solid ${danger}`, color: danger, fontFamily: fontMono, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", color: muted, fontFamily: fontMono, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Loading…
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {STATUS_COLUMNS.map((col) => {
            const colRows = rows.filter((r) => r.status === col.key);
            return (
              <div key={col.key} style={{ background: paper, border: `2px solid ${ink}`, minHeight: 200, display: "flex", flexDirection: "column" }}>
                <div style={{ padding: "12px 14px", borderBottom: `2px solid ${ink}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: white }}>
                  <span style={{ fontFamily: fontDisplay, fontSize: 14, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
                    {col.label}
                  </span>
                  <span style={{ fontFamily: fontMono, fontSize: 11, fontWeight: 700, padding: "2px 8px", border: `1px solid ${ink}`, color: col.color, letterSpacing: "0.06em" }}>
                    {counts[col.key] || 0}
                  </span>
                </div>
                <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                  {colRows.length === 0 && (
                    <div style={{ padding: 14, textAlign: "center", color: muted, fontFamily: fontMono, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      —
                    </div>
                  )}
                  {colRows.map((r) => (
                    <Card key={r.id} row={r} onClick={() => setSelected(r)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <Detail
          row={selected}
          employees={employees}
          onClose={() => setSelected(null)}
          onUpdate={updateStatus}
          onDelete={removeRow}
        />
      )}

      {settingsOpen && (
        <SettingsModal onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}

function Card({ row, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: white,
        border: `2px solid ${ink}`,
        padding: "12px 14px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        fontFamily: "'Archivo', sans-serif",
        color: ink,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
        <span style={{ fontFamily: fontDisplay, fontSize: 14, letterSpacing: "-0.01em", textTransform: "uppercase", lineHeight: 1.1 }}>
          {row.name}
        </span>
        <span style={{ fontFamily: fontMono, fontSize: 9, color: muted, letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
          {fmtAgo(row.createdAt)}
        </span>
      </div>
      {row.company && (
        <div style={{ fontFamily: fontMono, fontSize: 11, color: muted, letterSpacing: "0.04em" }}>
          {row.company}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontFamily: fontMono, fontSize: 10, color: muted, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {row.preferredDate && <span>📅 {row.preferredDate}</span>}
        {row.preferredTime && <span>· {fmtSlot(row.preferredTime)}</span>}
        {row.groupSize && <span>· {row.groupSize}</span>}
      </div>
      {row.assignedTo && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
          <span style={{ fontFamily: fontMono, fontSize: 10, fontWeight: 700, color: accent, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 6px", border: `1px solid ${accent}` }}>
            {row.assignedTo}
          </span>
        </div>
      )}
    </button>
  );
}

function parseConfirmed(cf) {
  // Splits a stored confirmed_for into machine-friendly date + time when
  // possible. Falls back to leaving it as raw text so old free-text rows
  // remain visible without forcing the admin to re-pick.
  if (!cf) return { date: "", time: "", raw: "" };
  const m = cf.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  if (m) return { date: m[1], time: m[2], raw: "" };
  return { date: "", time: "", raw: cf };
}

function Detail({ row, employees, onClose, onUpdate, onDelete }) {
  const initial = parseConfirmed(row.confirmedFor);
  const [internalNotes, setInternalNotes] = useState(row.internalNotes || "");
  const [confDate, setConfDate] = useState(initial.date || row.preferredDate || "");
  const [confTime, setConfTime] = useState(initial.time);
  const [confRaw, setConfRaw] = useState(initial.raw);
  const [assignedTo, setAssignedTo] = useState(row.assignedTo || "");
  const [avail, setAvail] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [smsLog, setSmsLog] = useState([]);

  const fetchSmsLog = useCallback(() => {
    fetch(`${API_URL}/api/sms-log?tourRequestId=${row.id}`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setSmsLog(data); })
      .catch(() => {});
  }, [row.id]);

  useEffect(() => {
    setInternalNotes(row.internalNotes || "");
    const parsed = parseConfirmed(row.confirmedFor);
    setConfDate(parsed.date || row.preferredDate || "");
    setConfTime(parsed.time);
    setConfRaw(parsed.raw);
    setAssignedTo(row.assignedTo || "");
    fetchSmsLog();
  }, [row, fetchSmsLog]);

  useEffect(() => {
    if (!confDate) {
      setAvail(null);
      return;
    }
    let cancel = false;
    setAvailLoading(true);
    fetch(`${API_URL}/api/tour-availability?date=${encodeURIComponent(confDate)}`)
      .then((r) => r.json())
      .then((data) => { if (!cancel) setAvail(data); })
      .catch(() => { if (!cancel) setAvail({ open: false, slots: [], booked: [], reason: "unavailable" }); })
      .finally(() => { if (!cancel) setAvailLoading(false); });
    return () => { cancel = true; };
  }, [confDate]);

  const buildConfirmedFor = () => {
    if (confDate && confTime) return `${confDate} ${confTime}`;
    return confRaw || "";
  };

  const persistMeta = () => {
    onUpdate(row.id, {
      internalNotes,
      confirmedFor: buildConfirmedFor(),
      assignedTo,
    });
  };

  const confirmTour = () => {
    // confirmed_for is optional — a tour can be confirmed informally
    // without holding a specific slot. If date+time are picked, they
    // get persisted (and block that slot from future bookings).
    onUpdate(row.id, {
      status: "confirmed",
      confirmedFor: buildConfirmedFor(),
      assignedTo,
      internalNotes,
    });
    // Backend fires SMS on the status transition; refresh the log a
    // couple seconds later to surface the new entries.
    setTimeout(fetchSmsLog, 1500);
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 500,
        background: "rgba(10,10,15,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, overflowY: "auto",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: paper, border: `2px solid ${ink}`,
          width: "100%", maxWidth: 640,
          padding: 28, position: "relative",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 14, right: 14,
            width: 36, height: 36, border: `2px solid ${ink}`,
            background: paper, cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>

        <div style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: accent, fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 6, height: 6, background: accent, display: "inline-block" }} />
          Tour Request · #{row.id}
        </div>
        <h3 style={{ fontFamily: fontDisplay, fontSize: 28, letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 6 }}>
          {row.name}
        </h3>
        <div style={{ fontFamily: fontMono, fontSize: 12, color: muted, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 18 }}>
          {row.company && `${row.company} · `}
          submitted {fmtAgo(row.createdAt)}
        </div>

        <Field label="Email">
          <a href={`mailto:${row.email}`} style={{ color: accent, fontFamily: fontMono, fontSize: 13, fontWeight: 700, textDecoration: "underline" }}>
            {row.email}
          </a>
        </Field>
        {row.phone && (
          <Field label="Phone">
            <a href={`tel:${row.phone}`} style={{ color: accent, fontFamily: fontMono, fontSize: 13, fontWeight: 700, textDecoration: "underline" }}>
              {row.phone}
            </a>
          </Field>
        )}
        <Field label="SMS opt-in">
          {row.smsConsent ? (
            <span style={{ color: success, fontWeight: 700, fontFamily: fontMono, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              ✓ Opted in
            </span>
          ) : (
            <span style={{ color: muted, fontFamily: fontMono, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Not opted in — text reminders won&apos;t send
            </span>
          )}
        </Field>
        <Field label="Preferred">
          {row.preferredDate || "—"} {row.preferredTime ? ` · ${fmtSlot(row.preferredTime)}` : ""}
          {row.groupSize ? ` · ${row.groupSize}` : ""}
        </Field>
        {row.notes && (
          <Field label="Customer notes">
            <div style={{ whiteSpace: "pre-wrap", fontFamily: "'Archivo', sans-serif", fontSize: 14, lineHeight: 1.5 }}>
              {row.notes}
            </div>
          </Field>
        )}

        {/* Schedule */}
        <div style={{ marginTop: 20, paddingTop: 18, borderTop: `2px solid ${ink}` }}>
          <div style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: 12 }}>
            Schedule
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <FieldEditable label="Confirmed date" value={confDate} onChange={setConfDate} type="date" />
            <SlotSelect
              label="Confirmed time slot"
              value={confTime}
              onChange={setConfTime}
              avail={avail}
              loading={availLoading}
              date={confDate}
            />
          </div>
          {confRaw && (
            <FieldEditable
              label="Legacy confirmed_for value"
              value={confRaw}
              onChange={setConfRaw}
              placeholder="Free-text from before slot picker"
            />
          )}
        </div>

        {/* Editable fields */}
        <div style={{ marginTop: 20, paddingTop: 18, borderTop: `2px solid ${ink}` }}>
          <div style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: 12 }}>
            Internal
          </div>
          <EmployeeSelect label="Assigned to" value={assignedTo} onChange={setAssignedTo} employees={employees} />
          <FieldEditable label="Internal notes" value={internalNotes} onChange={setInternalNotes} textarea placeholder="Anything the team should know" />
          <button onClick={persistMeta} style={{ ...S.saveJobBtn, width: "auto", padding: "10px 22px", marginTop: 4 }}>
            Save Notes
          </button>
        </div>

        {/* SMS log */}
        {smsLog.length > 0 && (
          <div style={{ marginTop: 20, paddingTop: 18, borderTop: `2px solid ${ink}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, fontWeight: 700 }}>
                SMS log · {smsLog.length}
              </div>
              <button onClick={fetchSmsLog} style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: muted, background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                Refresh
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {smsLog.map((m) => (
                <SmsLogItem key={m.id} m={m} />
              ))}
            </div>
          </div>
        )}

        {/* Status actions */}
        <div style={{ marginTop: 20, paddingTop: 18, borderTop: `2px solid ${ink}`, display: "flex", flexWrap: "wrap", gap: 8 }}>
          <StatusBtn current={row.status} target="new" label="Mark New" onClick={() => onUpdate(row.id, { status: "new" })} />
          <StatusBtn current={row.status} target="confirmed" label="Confirm" onClick={confirmTour} color={success} />
          <StatusBtn current={row.status} target="completed" label="Complete" onClick={() => onUpdate(row.id, { status: "completed" })} />
          <StatusBtn current={row.status} target="cancelled" label="Cancel" onClick={() => onUpdate(row.id, { status: "cancelled" })} color={danger} />
          <button
            onClick={() => onDelete(row.id)}
            style={{
              padding: "10px 16px", border: `2px solid ${danger}`, background: "transparent",
              color: danger, fontFamily: "'Archivo', sans-serif", fontSize: 12, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.06em", cursor: "pointer", marginLeft: "auto",
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 14, color: ink }}>
        {children}
      </div>
    </div>
  );
}

function FieldEditable({ label, value, onChange, placeholder, textarea, type }) {
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontFamily: "'Archivo', sans-serif",
    fontSize: 14,
    background: white,
    color: ink,
    border: `2px solid ${ink}`,
    borderRadius: 0,
    outline: "none",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: 4 }}>
        {label}
      </div>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
      ) : (
        <input type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
      )}
    </div>
  );
}

function EmployeeSelect({ label, value, onChange, employees }) {
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontFamily: "'Archivo', sans-serif",
    fontSize: 14,
    background: white,
    color: ink,
    border: `2px solid ${ink}`,
    borderRadius: 0,
    outline: "none",
    cursor: "pointer",
  };
  // Stay backward-compatible with any existing free-text values: if the
  // saved name doesn't match a current employee, keep it visible as an
  // extra option rather than silently dropping it.
  const knownNames = (employees || []).map((e) => e.name).filter(Boolean);
  const showLegacy = value && !knownNames.includes(value);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: 4 }}>
        {label}
      </div>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      >
        <option value="">— Unassigned —</option>
        {(employees || []).map((emp) => (
          <option key={emp.id || emp.name} value={emp.name}>
            {emp.name}{emp.role ? ` · ${emp.role}` : ""}
          </option>
        ))}
        {showLegacy && (
          <option value={value}>{value} (legacy)</option>
        )}
      </select>
    </div>
  );
}

function SlotSelect({ label, value, onChange, avail, loading, date }) {
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontFamily: "'Archivo', sans-serif",
    fontSize: 14,
    background: white,
    color: ink,
    border: `2px solid ${ink}`,
    borderRadius: 0,
    outline: "none",
    cursor: "pointer",
  };
  const allSlots = avail?.allSlots || avail?.slots || [];
  const booked = new Set(avail?.booked || []);
  // Always allow the currently saved value to remain selectable so admins
  // can re-save without re-picking, even if the slot is "self-booked" by
  // this same request.
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: 4 }}>
        {label}
      </div>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={!date || loading}
        style={inputStyle}
      >
        <option value="">{loading ? "Loading…" : !date ? "Pick a date first" : avail && !avail.open ? "Closed that day" : "—"}</option>
        {allSlots.map((s) => {
          const isBooked = booked.has(s) && s !== value;
          return (
            <option key={s} value={s} disabled={isBooked}>
              {fmtSlot(s)}{isBooked ? " (booked)" : ""}
            </option>
          );
        })}
      </select>
    </div>
  );
}

function SettingsModal({ onClose }) {
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [newBlackout, setNewBlackout] = useState("");

  useEffect(() => {
    fetch(`${API_URL}/api/tour-settings`)
      .then((r) => r.json())
      .then((data) => {
        // Backend returns hours keyed by stringified day index. Normalize.
        const hours = {};
        Object.entries(data.hours || {}).forEach(([k, v]) => {
          hours[Number(k)] = v;
        });
        setCfg({
          openDays: data.openDays || [],
          hours: { ...DEFAULT_CONFIG.hours, ...hours },
          slotMinutes: data.slotMinutes || 60,
          blackoutDates: data.blackoutDates || [],
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleDay = (dayIdx) => {
    setCfg((prev) => {
      const open = prev.openDays.includes(dayIdx);
      const openDays = open
        ? prev.openDays.filter((d) => d !== dayIdx)
        : [...prev.openDays, dayIdx].sort((a, b) => a - b);
      const hours = { ...prev.hours };
      if (!open && !hours[dayIdx]) hours[dayIdx] = { start: "09:00", end: "21:00" };
      return { ...prev, openDays, hours };
    });
  };

  const setDayHours = (dayIdx, key, val) => {
    setCfg((prev) => ({
      ...prev,
      hours: {
        ...prev.hours,
        [dayIdx]: { ...(prev.hours[dayIdx] || { start: "09:00", end: "21:00" }), [key]: val },
      },
    }));
  };

  const addBlackout = () => {
    if (!newBlackout) return;
    setCfg((prev) => ({
      ...prev,
      blackoutDates: prev.blackoutDates.includes(newBlackout)
        ? prev.blackoutDates
        : [...prev.blackoutDates, newBlackout].sort(),
    }));
    setNewBlackout("");
  };

  const removeBlackout = (d) => {
    setCfg((prev) => ({ ...prev, blackoutDates: prev.blackoutDates.filter((x) => x !== d) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        openDays: cfg.openDays,
        hours: Object.fromEntries(
          Object.entries(cfg.hours).map(([k, v]) => [String(k), v])
        ),
        slotMinutes: Number(cfg.slotMinutes) || 60,
        blackoutDates: cfg.blackoutDates,
      };
      const res = await fetch(`${API_URL}/api/tour-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    padding: "8px 10px",
    fontFamily: "'Archivo', sans-serif",
    fontSize: 13,
    background: white,
    color: ink,
    border: `2px solid ${ink}`,
    borderRadius: 0,
    outline: "none",
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 600,
        background: "rgba(10,10,15,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, overflowY: "auto",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: paper, border: `2px solid ${ink}`,
          width: "100%", maxWidth: 720,
          padding: 28, position: "relative",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 14, right: 14,
            width: 36, height: 36, border: `2px solid ${ink}`,
            background: paper, cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>

        <div style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: accent, fontWeight: 700, marginBottom: 8 }}>
          Tour Settings
        </div>
        <h3 style={{ fontFamily: fontDisplay, fontSize: 28, letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 6 }}>
          Hours & Availability
        </h3>
        <div style={{ fontFamily: fontMono, fontSize: 12, color: muted, letterSpacing: "0.04em", marginBottom: 24 }}>
          Drives the slot picker on nycmanufacturing.com/tour. Confirmed bookings auto-block the chosen slot.
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: muted, fontFamily: fontMono, fontSize: 12, textTransform: "uppercase" }}>Loading…</div>
        ) : (
          <>
            {/* Slot duration */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: 6 }}>
                Slot duration (minutes)
              </div>
              <input
                type="number"
                min="15"
                step="15"
                value={cfg.slotMinutes}
                onChange={(e) => setCfg((p) => ({ ...p, slotMinutes: Number(e.target.value) }))}
                style={{ ...inputStyle, width: 120 }}
              />
            </div>

            {/* Days */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: 10 }}>
                Open days · hours
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {DAY_LABELS.map((dayLabel, idx) => {
                  const isOpen = cfg.openDays.includes(idx);
                  const h = cfg.hours[idx] || { start: "09:00", end: "21:00" };
                  return (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `2px solid ${isOpen ? ink : "rgba(10,10,15,0.18)"}`, background: isOpen ? white : "transparent" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={isOpen}
                          onChange={() => toggleDay(idx)}
                          style={{ width: 18, height: 18, accentColor: accent, cursor: "pointer" }}
                        />
                        <span style={{ fontFamily: fontDisplay, fontSize: 14, textTransform: "uppercase", letterSpacing: "-0.01em", minWidth: 50 }}>
                          {dayLabel}
                        </span>
                      </label>
                      <input
                        type="time"
                        value={h.start}
                        disabled={!isOpen}
                        onChange={(e) => setDayHours(idx, "start", e.target.value)}
                        style={{ ...inputStyle, opacity: isOpen ? 1 : 0.4 }}
                      />
                      <span style={{ fontFamily: fontMono, fontSize: 11, color: muted }}>to</span>
                      <input
                        type="time"
                        value={h.end}
                        disabled={!isOpen}
                        onChange={(e) => setDayHours(idx, "end", e.target.value)}
                        style={{ ...inputStyle, opacity: isOpen ? 1 : 0.4 }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Blackout dates */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: 10 }}>
                Blackout dates · holidays, off-days
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input
                  type="date"
                  value={newBlackout}
                  onChange={(e) => setNewBlackout(e.target.value)}
                  style={inputStyle}
                />
                <button onClick={addBlackout} style={{ ...inputStyle, cursor: "pointer", background: ink, color: white, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 12 }}>
                  Add
                </button>
              </div>
              {cfg.blackoutDates.length === 0 ? (
                <div style={{ fontFamily: fontMono, fontSize: 12, color: muted, padding: "8px 0" }}>
                  No blackouts set.
                </div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {cfg.blackoutDates.map((d) => (
                    <span key={d} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px 4px 10px", border: `2px solid ${ink}`, fontFamily: fontMono, fontSize: 12, fontWeight: 700 }}>
                      {d}
                      <button onClick={() => removeBlackout(d)} style={{ width: 18, height: 18, border: "none", background: "transparent", cursor: "pointer", color: danger, fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 18, borderTop: `2px solid ${ink}` }}>
              <div style={{ fontFamily: fontMono, fontSize: 11, color: muted, letterSpacing: "0.04em" }}>
                {savedAt ? `Saved ${fmtAgo(new Date(savedAt).toISOString())}` : ""}
              </div>
              <button
                onClick={save}
                disabled={saving}
                style={{ ...S.saveJobBtn, padding: "12px 28px", opacity: saving ? 0.6 : 1 }}
              >
                {saving ? "Saving…" : "Save Settings"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SmsLogItem({ m }) {
  const statusColor =
    m.status === "delivered" ? success :
    m.status === "failed" || m.status === "undelivered" || m.status === "error" ? danger :
    m.status === "canceled" ? muted :
    m.status === "scheduled" ? accent :
    ink;
  return (
    <div style={{ border: `2px solid ${ink}`, padding: "10px 12px", background: white, fontFamily: "'Archivo', sans-serif", fontSize: 13, color: ink }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: fontMono, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: muted }}>
          {(m.kind || "sms").replace(/_/g, " ")}
        </span>
        <span style={{ fontFamily: fontMono, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: statusColor, padding: "2px 6px", border: `1px solid ${statusColor}` }}>
          {m.status || "—"}
        </span>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.4, color: ink }}>{m.body}</div>
      <div style={{ marginTop: 6, fontFamily: fontMono, fontSize: 10, color: muted, letterSpacing: "0.04em" }}>
        {m.to ? `To ${m.to} · ` : ""}{m.sendAt ? `scheduled ${new Date(m.sendAt).toLocaleString()}` : `created ${fmtAgo(m.createdAt)}`}
      </div>
      {m.error && (
        <div style={{ marginTop: 4, fontFamily: fontMono, fontSize: 10, color: danger }}>
          {m.error}
        </div>
      )}
    </div>
  );
}

function StatusBtn({ current, target, label, onClick, color = accent }) {
  const active = current === target;
  return (
    <button
      onClick={onClick}
      disabled={active}
      style={{
        padding: "10px 16px",
        border: `2px solid ${color}`,
        background: active ? color : "transparent",
        color: active ? white : color,
        fontFamily: "'Archivo', sans-serif",
        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        cursor: active ? "default" : "pointer",
        opacity: active ? 0.85 : 1,
      }}
    >
      {active ? `✓ ${label}` : label}
    </button>
  );
}
